import React, { useCallback, useContext, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Bar, Doughnut } from "react-chartjs-2";
import {
  Chart,
  ArcElement,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
} from "chart.js";
import {
  FiUsers,
  FiFolder,
  FiClock,
  FiArrowRight,
  FiPlus,
  FiCalendar,
  FiCheckCircle,
  FiGift,
  FiRefreshCw,
  FiCreditCard,
  FiExternalLink,
  FiList,
  FiDatabase,
  FiFileText,
  FiDownload,
  FiEye,
  FiChevronLeft,
  FiChevronRight,
  FiFlag,
} from "react-icons/fi";
import { GlobalContext } from "../../Context/GlobalState";
import { billpunchbasicauth } from "../../utils/Credentials";
import { BASE_URL_API_TRANXT_UAT_API } from "../../utils/Constant";
import "./AdminDashboard.css";
import {
  attendanceCounts,
  uniqueTasks,
  parseJoiningDate,
} from "./dashboardMetrics";

Chart.register(
  ArcElement,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
);

function useDashboardData(endpoint, body, enabled) {
  const requestBody = JSON.stringify(body);
  const [state, setState] = useState({ data: [], loading: true, error: false });
  const refresh = useCallback(async () => {
    if (!enabled) return;
    setState((current) => ({ ...current, loading: true, error: false }));
    try {
      const { username, password } = billpunchbasicauth;
      const response = await fetch(
        `${BASE_URL_API_TRANXT_UAT_API}${endpoint}`,
        {
          method: "POST",
          headers: {
            Authorization: `Basic ${btoa(`${username}:${password}`)}`,
            "Content-Type": "application/json",
          },
          body: requestBody,
        },
      );
      if (!response.ok) throw new Error("Request failed");
      const result = await response.json();
      if (
        String(result.status) !== "0" &&
        !(
          endpoint === "/api/Salary/GetMarkAttendance" &&
          String(result.status) === "1"
        )
      )
        throw new Error("Unable to load data");
      setState({
        data: Array.isArray(result.data) ? result.data : [],
        loading: false,
        error: false,
      });
    } catch (error) {
      setState((current) => ({ ...current, loading: false, error: true }));
    }
  }, [endpoint, requestBody, enabled]);
  useEffect(() => {
    refresh();
  }, [refresh]);
  return { ...state, enabled, refresh };
}

function DataState({ source, empty, children }) {
  if (source.loading)
    return (
      <div
        className="ad-skeleton"
        role="status"
        aria-label="Loading dashboard data"
      />
    );
  if (source.error)
    return (
      <div className="ad-data-message" role="alert">
        Unable to load this section.{" "}
        <button type="button" onClick={source.refresh}>
          Retry
        </button>
      </div>
    );
  if (!source.data.length)
    return <p className="ad-data-message">{empty || "No data available."}</p>;
  return children;
}
const number = (value) =>
  Number.isFinite(Number(value)) ? Number(value).toLocaleString("en-IN") : "—";
const money = (value) =>
  Number.isFinite(Number(value))
    ? Number(value).toLocaleString("en-IN", {
        style: "currency",
        currency: "INR",
        maximumFractionDigits: 0,
      })
    : "—";

export default function AdminDashboard() {
  const { user, accessData } = useContext(GlobalContext);
  const can = (moduleName, roleName) =>
    (accessData || []).some(
      (module) =>
        module.module_name === moduleName &&
        (module.data || []).some(
          (role) => role.role_name?.trim() === roleName && role.access === "Y",
        ),
    );
  const permissions = {
    people: can("Employee Management", "Employee Management"),
    attendance: can("Attendance Management", "Attendance Management"),
    leave: can("Leave Management", "Approve/Reject Leave"),
    projects: can("Project Management", "View Projects"),
    payroll: can("Payroll Management", "View Payroll"),
    policy: can("Policy Management", "View Policy"),
    expenses: can("Expense Payroll Management", "View Expense Payroll"),
  };
  const allowedRoute = (route) =>
    ({
      "/admin/employee-management": permissions.people,
      "/admin/attendance-management": permissions.attendance,
      "/admin/LeaveApprovalAdmin": permissions.leave,
      "/admin/work-dashboard": permissions.projects,
      "/admin/create-workspace": permissions.projects,
      "/admin/payroll-management": permissions.payroll,
      "/admin/expense-payroll": permissions.expenses,
    })[route] ?? true;
  const enabled = Boolean(user?.firm_id);
  const firmid = user?.firm_id;
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth());
  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];
  const people = useDashboardData(
    "/api/Salary/getEmployee",
    { firmId: firmid },
    enabled && permissions.people,
  );
  const leaves = useDashboardData(
    "/api/Salary/GetAllEmployeeLeave",
    { firmid },
    enabled && permissions.leave,
  );
  const payouts = useDashboardData(
    "/api/Salary/GetPayoutDashboard",
    { firmid, month: "0", year: String(year) },
    enabled && permissions.payroll,
  );
  const expenses = useDashboardData(
    "/api/Salary/GetFixedExpenseDashboard",
    { firmid, month: monthNames[month], year: String(year) },
    enabled && permissions.expenses,
  );
  const dateKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
  const [taskFrom, setTaskFrom] = useState(`${dateKey.slice(0, 7)}-01`);
  const monthEnd = `${dateKey.slice(0, 7)}-${new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate()}`;
  const [taskTo, setTaskTo] = useState(monthEnd);
  const attendance = useDashboardData(
    "/api/Salary/GetMarkAttendance",
    {
      id: firmid,
      month: String(now.getMonth() + 1),
      year: String(now.getFullYear()),
    },
    enabled && permissions.attendance,
  );
  const wfh = useDashboardData(
    "/api/SuperAdmin/GetWfhV1",
    { empid: "string", firmid, reporting_id: user?.userid, type: "2" },
    enabled && permissions.leave && Boolean(user?.userid),
  );
  const tasks = useDashboardData(
    "/api/SuperAdmin/GetTaskSubtaskOverview",
    { firmid, fromdate: taskFrom, todate: taskTo },
    enabled,
  );
  const taskList = uniqueTasks(tasks.data);
  const taskCounts = Object.fromEntries(
    ["P", "O", "C"].map((status) => [
      status,
      taskList.filter((task) => task.task_status === status).length,
    ]),
  );
  const attendanceTotals = attendanceCounts(attendance.data, dateKey);
  const overlapsCurrentMonth = (from, to = from) => {
    const start = Date.parse(from);
    const end = Date.parse(to || from);
    return (
      Number.isFinite(start) &&
      Number.isFinite(end) &&
      end >= new Date(`${dateKey.slice(0, 7)}-01T00:00:00`).getTime() &&
      start <= new Date(`${monthEnd}T23:59:59.999`).getTime()
    );
  };
  const pendingWfh = wfh.data.filter(
    (item) => item.status === "N" && overlapsCurrentMonth(item.date),
  );
  const approvals = {
    data: [...leaves.data, ...wfh.data],
    loading: leaves.loading || wfh.loading,
    error: leaves.error || wfh.error,
    refresh: () => {
      leaves.refresh();
      wfh.refresh();
    },
  };
  const workspaces = useDashboardData(
    "/api/Salary/GetYourWorkspace",
    { empid: "0", firmid, ownerid: user?.userid },
    enabled && permissions.projects && Boolean(user?.userid),
  );
  const policies = useDashboardData(
    "/api/Salary/GetFirmPolicy",
    { firmid },
    enabled && permissions.policy,
  );
  const sources = [
    people,
    leaves,
    payouts,
    expenses,
    attendance,
    wfh,
    tasks,
    workspaces,
    policies,
  ].filter((source) => source.enabled);
  const pendingLeaves = leaves.data.filter(
    (item) =>
      item.status === "P" && overlapsCurrentMonth(item.form_date, item.to_date),
  );

  const birthdays = people.data
    .filter(
      (person) =>
        person.dob && new Date(person.dob).getMonth() === now.getMonth(),
    )
    .sort((a, b) => new Date(a.dob).getDate() - new Date(b.dob).getDate());
  const summary = [
    {
      label: "Total Employees",
      value: people.data.length,
      source: people,
      icon: FiUsers,
      tone: "blue",
      to: "/admin/employee-management",
      detail: "People in your organization",
    },
    {
      label: "Present Today",
      value: attendanceTotals.present,
      source: attendance,
      icon: FiCalendar,
      tone: "teal",
      to: "/admin/attendance-management",
      detail: "Includes late and WFH records",
    },
    {
      label: "Pending Approvals",
      value: pendingLeaves.length + pendingWfh.length,
      source: approvals,
      icon: FiClock,
      tone: "pink",
      to: "/admin/LeaveApprovalAdmin",
      detail: "Current month · leave and WFH",
    },
    {
      label: "Overdue Tasks",
      value: taskCounts.O,
      source: tasks,
      icon: FiClock,
      tone: "orange",
      to: "/admin/task-overview",
      detail: "Within the selected task period",
    },
    {
      label: "Workspaces",
      value: workspaces.data.length,
      source: workspaces,
      icon: FiDatabase,
      tone: "purple",
      to: "/admin/work-dashboard",
      detail: "Workspaces available to you",
    },
  ];
  const chartData = {
    labels: payouts.data.map((item) => item.month),
    datasets: [
      {
        label: "Full-time salary",
        data: payouts.data.map((item) => Number(item.totalFullTimeSalary) || 0),
        backgroundColor: "#347df4",
        borderRadius: 4,
        maxBarThickness: 18,
      },
      {
        label: "Intern salary",
        data: payouts.data.map((item) => Number(item.totalInternSalary) || 0),
        backgroundColor: "#a8caff",
        borderRadius: 4,
        maxBarThickness: 18,
      },
    ],
  };
  const payroll = expenses.data[0] || {};
  const [period, setPeriod] = useState("month");
  const [weekOffset, setWeekOffset] = useState(0);
  const changePeriod = (value) => {
    setPeriod(value);
    const start = new Date(
      now.getFullYear(),
      now.getMonth(),
      value === "month" ? 1 : now.getDate() - 6,
    );
    setTaskFrom(
      `${start.getFullYear()}-${String(start.getMonth() + 1).padStart(2, "0")}-${String(start.getDate()).padStart(2, "0")}`,
    );
    setTaskTo(value === "month" ? monthEnd : dateKey);
  };
  const colors = ["#31cd8e", "#3389ff", "#ffbd38", "#fb536c"];
  const statusDefinitions = [
    { code: "C", label: "Completed", color: colors[0] },
    { code: "P", label: "Pending", color: colors[2] },
    { code: "O", label: "Overdue", color: colors[3] },
  ];
  const knownCount = Object.values(taskCounts).reduce(
    (sum, count) => sum + count,
    0,
  );
  const unknownCount = taskList.length - knownCount;
  const doughnutData = {
    labels: [
      ...statusDefinitions.map((item) => item.label),
      ...(unknownCount ? ["Other"] : []),
    ],
    datasets: [
      {
        data: [
          ...statusDefinitions.map((item) => taskCounts[item.code]),
          ...(unknownCount ? [unknownCount] : []),
        ],
        backgroundColor: [
          ...statusDefinitions.map((item) => item.color),
          "#a8b7ce",
        ],
        borderWidth: 0,
        hoverOffset: 3,
      },
    ],
  };
  const doughnutOptions = {
    maintainAspectRatio: false,
    cutout: "76%",
    plugins: { legend: { display: false } },
  };
  const recorded =
    attendanceTotals.present +
    attendanceTotals.absent +
    attendanceTotals.halfDay +
    attendanceTotals.onLeave;
  const presentPercentage = recorded
    ? Math.round((attendanceTotals.present / recorded) * 100)
    : 0;
  const attendanceChart = {
    labels: ["Present", "Absent", "Half Day", "On Leave"],
    datasets: [
      {
        data: [
          attendanceTotals.present,
          attendanceTotals.absent,
          attendanceTotals.halfDay,
          attendanceTotals.onLeave,
        ],
        backgroundColor: [
          "#24ce8c",
          "#fb536c",
          "#779bd0",
          "#cddcf0",
          "#e9eef5",
        ],
        borderWidth: 0,
      },
    ],
  };
  const recentTasks = [...taskList]
    .sort(
      (a, b) =>
        (Date.parse(b.task_startdate) || 0) -
        (Date.parse(a.task_startdate) || 0),
    )
    .slice(0, 5);
  const projectGroups = new Map();
  taskList.forEach((task) => {
    const name = task.project_name || "Unassigned project";
    if (!projectGroups.has(name)) projectGroups.set(name, []);
    projectGroups.get(name).push(task);
  });
  const topProjects = [...projectGroups.entries()]
    .sort((a, b) => b[1].length - a[1].length)
    .slice(0, 5);
  const projectStatusChart = {
    labels: topProjects.map(([name]) => name),
    datasets: statusDefinitions.map((item) => ({
      label: item.label,
      data: topProjects.map(
        ([, records]) =>
          records.filter((task) => task.task_status === item.code).length,
      ),
      backgroundColor: item.color,
      borderRadius: 3,
      maxBarThickness: 14,
    })),
  };
  const datedEmployees = people.data
    .map((person) =>
      parseJoiningDate(
        person.joiningdate ||
          person.joining_date ||
          person.date_of_joining ||
          person.doj,
      ),
    )
    .filter(Boolean);
  const trendMonths = Array.from(
    { length: 6 },
    (_, index) => new Date(now.getFullYear(), now.getMonth() - 5 + index, 1),
  );
  const monthlyJoiners = trendMonths.map(
    (month) =>
      datedEmployees.filter(
        (date) =>
          date.getFullYear() === month.getFullYear() &&
          date.getMonth() === month.getMonth(),
      ).length,
  );
  const monthlyTotals = trendMonths.map((month) => {
    // The current month uses the complete roster, including missing joining dates.
    if (
      month.getFullYear() === now.getFullYear() &&
      month.getMonth() === now.getMonth()
    )
      return people.data.length;
    const end = new Date(
      month.getFullYear(),
      month.getMonth() + 1,
      0,
      23,
      59,
      59,
      999,
    );
    return datedEmployees.filter((date) => date <= end).length;
  });
  const employeeTrend = {
    labels: trendMonths.map((date) =>
      date.toLocaleDateString("en-GB", { month: "short" }),
    ),
    datasets: [
      {
        label: "Total Employees",
        data: monthlyTotals.map(
          (total, index) => total - monthlyJoiners[index],
        ),
        backgroundColor: "#4597ff",
        borderRadius: { topLeft: 3, topRight: 3 },
        borderSkipped: false,
        maxBarThickness: 28,
        order: 2,
      },
      {
        label: "New Joiners",
        data: monthlyJoiners,
        backgroundColor: "#3edb96",
        borderRadius: 0,
        maxBarThickness: 28,
        order: 1,
      },
    ],
  };
  const employeeTrendOptions = {
    maintainAspectRatio: false,
    layout: { padding: { top: 22, right: 8 } },
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (context) =>
            `${context.dataset.label}: ${context.datasetIndex === 0 ? monthlyTotals[context.dataIndex] : monthlyJoiners[context.dataIndex]}`,
        },
      },
    },
    scales: {
      x: {
        stacked: true,
        grid: { display: false },
        border: { display: false },
        ticks: { font: { size: 10 }, color: "#7b8da8" },
      },
      y: {
        stacked: true,
        beginAtZero: true,
        suggestedMax: Math.max(...monthlyTotals, 1) * 1.2,
        ticks: {
          precision: 0,
          maxTicksLimit: 4,
          font: { size: 10 },
          color: "#7b8da8",
        },
        grid: { color: "#edf2f9" },
        border: { color: "#b7c6dc" },
      },
    },
  };
  const employeeTotalLabels = {
    id: "employeeTotalLabels",
    afterDatasetsDraw(chart) {
      const { ctx, scales } = chart;
      ctx.save();
      ctx.font = "600 11px Arial";
      ctx.fillStyle = "#293d5b";
      ctx.textAlign = "center";
      monthlyTotals.forEach((total, index) =>
        ctx.fillText(
          String(total),
          scales.x.getPixelForValue(index),
          scales.y.getPixelForValue(total) - 8,
        ),
      );
      const joinerBars = chart.getDatasetMeta(1).data;
      ctx.font = "600 10px Arial";
      monthlyJoiners.forEach((count, index) => {
        if (!count || !joinerBars[index]) return;
        const bar = joinerBars[index];
        ctx.fillStyle = "#16734f";
        ctx.fillText(String(count), bar.x + 21, (bar.y + bar.base) / 2 + 3);
      });
      ctx.restore();
    },
  };
  const chartOptions = {
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "top",
        align: "end",
        labels: { usePointStyle: true, boxWidth: 7, font: { size: 9 } },
      },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { font: { size: 10 } },
        border: { display: false },
      },
      y: {
        beginAtZero: true,
        ticks: { precision: 0, font: { size: 10 } },
        grid: { color: "#edf2f9" },
        border: { display: false },
      },
    },
  };
  const weekStart = new Date(now);
  weekStart.setDate(now.getDate() - now.getDay() + weekOffset * 7);
  const approvalItems = [
    ...pendingLeaves.map((item) => ({
      name: item.employee_name,
      type: "Leave request",
      id: item.pk_leave_id,
    })),
    ...pendingWfh.map((item) => ({
      name: item.fullname,
      type: "WFH request",
      id: item.pk_id,
    })),
  ];
  const heading = (Icon, title, to) => (
    <div className="ar-heading">
      <h2>
        <span className="ar-icon">
          <Icon />
        </span>
        {title}
      </h2>
      {to && (
        <Link to={to}>
          View All
          <FiArrowRight />
        </Link>
      )}
    </div>
  );
  const stat = (source, value) =>
    source.loading ? "…" : source.error ? "—" : number(value);
  return (
    <div className="ar-page">
      <header className="ar-hero">
        <div>
          <p>
            Good{" "}
            {now.getHours() < 12
              ? "Morning"
              : now.getHours() < 17
                ? "Afternoon"
                : "Evening"}
            ,
          </p>
          <h1>
            {user?.username || "Admin"} <span aria-hidden="true">👋</span>
          </h1>
          <small>
            Here's what's happening in your organization this month.
          </small>
        </div>
        <div className="ar-hero-art">
          <AdminProfilePhoto
            imageUrl={user?.profile_url}
            name={user?.username || "Admin"}
          />
          <blockquote>
            “Plan your work,
            <br />
            track your progress,
            <br />
            make it happen.”
          </blockquote>
        </div>
        <div className="ar-hero-tools">
          <button
            type="button"
            aria-label="Refresh dashboard"
            onClick={() => sources.forEach((source) => source.refresh())}
          >
            <FiRefreshCw />
          </button>
        </div>
      </header>
      <div className="ar-summary-grid">
        {summary
          .filter((item) => allowedRoute(item.to))
          .map(({ label, value, source, icon: Icon, tone, to, detail }) => (
            <Link key={label} to={to} className={`ar-summary ar-${tone}`}>
              <span className="ar-summary-icon">
                <Icon />
              </span>
              <div className="ar-summary-copy">
                <p>{label}</p>
                <strong>{stat(source, value)}</strong>
                <small>{source.error ? "Unable to load" : detail}</small>
              </div>
              <svg
                className="ar-summary-wave"
                viewBox="0 0 240 36"
                preserveAspectRatio="none"
                aria-hidden="true"
                focusable="false"
              >
                <defs>
                  <linearGradient
                    id={`summary-wave-${tone}`}
                    x1="0"
                    y1="0"
                    x2="0"
                    y2="1"
                  >
                    <stop
                      offset="0%"
                      stopColor="currentColor"
                      stopOpacity=".23"
                    />
                    <stop
                      offset="100%"
                      stopColor="currentColor"
                      stopOpacity="0"
                    />
                  </linearGradient>
                </defs>
                <path
                  d="M0 24 C16 24 20 26 34 22 S53 23 67 17 S88 9 101 13 S122 25 138 18 S156 24 174 16 S202 8 216 11 S230 16 240 13 V36 H0Z"
                  fill={`url(#summary-wave-${tone})`}
                />
                <path
                  d="M0 24 C16 24 20 26 34 22 S53 23 67 17 S88 9 101 13 S122 25 138 18 S156 24 174 16 S202 8 216 11 S230 16 240 13"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.4"
                  vectorEffect="non-scaling-stroke"
                />
              </svg>
            </Link>
          ))}
      </div>
      <div className="ar-layout">
        <main className="ar-main">
          <div className="ar-top-row">
            {permissions.people && (
              <section className="ar-card ar-trend">
                <div className="ar-employee-trend-heading">
                  <div>
                    {heading(FiUsers, "Employee Trend")}
                    <p className="ar-note">
                      Monthly employee count and new joiners
                    </p>
                  </div>
                  <div className="ar-trend-legend">
                    <span>
                      <i />
                      Total Employees
                    </span>
                    <span>
                      <i />
                      New Joiners
                    </span>
                  </div>
                </div>
                {!people.loading && !people.error && (
                  <p className="ar-joiner-count">
                    New joiners this month:{" "}
                    <strong>{monthlyJoiners[monthlyJoiners.length - 1]}</strong>
                  </p>
                )}
                <DataState source={people}>
                  {people.data.length ? (
                    <div className="ar-chart">
                      <Bar
                        data={employeeTrend}
                        options={employeeTrendOptions}
                        plugins={[employeeTotalLabels]}
                      />
                    </div>
                  ) : (
                    <p className="ar-empty">
                      Joining dates are unavailable for the employee trend.
                    </p>
                  )}
                </DataState>
                {!people.loading && !people.error && people.data.length > 0 && (
                  <p className="ar-trend-source">
                    Current month: full roster ({people.data.length}). Earlier
                    months and new joiners use available joining dates
                    {people.data.length > datedEmployees.length
                      ? `; ${people.data.length - datedEmployees.length} missing dates excluded from historical counts`
                      : ""}
                    .
                  </p>
                )}
              </section>
            )}
            <section className="ar-card ar-task-overview">
              <div className="ar-heading">
                <h2>
                  <span className="ar-icon">
                    <FiList />
                  </span>
                  Task Overview
                </h2>
                <select
                  aria-label="Task period"
                  value={period}
                  onChange={(event) => changePeriod(event.target.value)}
                >
                  <option value="month">This Month</option>
                  <option value="week">Last 7 Days</option>
                </select>
              </div>
              <DataState source={tasks} empty="No tasks in this period.">
                <div className="ar-doughnut-row">
                  <div className="ar-doughnut">
                    <Doughnut data={doughnutData} options={doughnutOptions} />
                    <div className="ar-chart-center">
                      <strong>{taskList.length}</strong>
                      <small>Total Tasks</small>
                    </div>
                  </div>
                  <div className="ar-legend">
                    {statusDefinitions.map((item) => (
                      <div key={item.code}>
                        <i style={{ background: item.color }} />
                        <span>{item.label}</span>
                        <strong>{taskCounts[item.code]}</strong>
                        <small>
                          (
                          {taskList.length
                            ? Math.round(
                                (taskCounts[item.code] / taskList.length) * 100,
                              )
                            : 0}
                          %)
                        </small>
                      </div>
                    ))}
                    {unknownCount > 0 && (
                      <div>
                        <i style={{ background: "#a8b7ce" }} />
                        <span>Other</span>
                        <strong>{unknownCount}</strong>
                      </div>
                    )}
                  </div>
                </div>
              </DataState>
            </section>
          </div>
          <div className="ar-middle-row">
            {permissions.leave && (
              <section className="ar-card">
                {heading(
                  FiClock,
                  "Unified Approvals",
                  "/admin/LeaveApprovalAdmin",
                )}
                <DataState
                  source={approvals}
                  empty="No approval requests available."
                >
                  {approvalItems.length ? (
                    <div className="ar-approval-list">
                      {approvalItems.slice(0, 4).map((item, index) => (
                        <div
                          className="ar-approval"
                          key={`${item.type}-${item.id || index}`}
                        >
                          <span className="ar-avatar">
                            {String(item.name || "?").charAt(0)}
                          </span>
                          <div>
                            <strong>{item.name || "Employee"}</strong>
                            <p>{item.type}</p>
                          </div>
                          <span className="ar-badge ar-pending">Pending</span>
                          <Link to="/admin/LeaveApprovalAdmin">
                            Review
                            <FiArrowRight />
                          </Link>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="ar-empty">
                      No pending leave or WFH requests.
                    </p>
                  )}
                </DataState>
                <p className="ar-note">
                  Current month · WFH requests assigned to your reporting team.
                </p>
              </section>
            )}
            <section className="ar-card">
              {heading(FiList, "Recent Tasks", "/admin/task-overview")}
              <DataState source={tasks} empty="No tasks in this period.">
                <div className="ar-task-list">
                  {recentTasks.map((task, index) => (
                    <Link
                      to="/admin/task-overview"
                      className="ar-task"
                      key={task.pk_taskid || index}
                    >
                      <FiFlag
                        style={{
                          color:
                            task.task_priority === "Urgent"
                              ? "#fa4b66"
                              : "#ff9d36",
                        }}
                      />
                      <div>
                        <strong>{task.taskname}</strong>
                        <p>{task.workspace_name || task.project_name || "—"}</p>
                      </div>
                      <span className="ar-avatar" title={task.task_assignee}>
                        {String(task.task_assignee || "?").charAt(0)}
                      </span>
                      <span
                        className={`ar-badge ar-status-${task.task_status}`}
                      >
                        {{ C: "Completed", P: "Pending", O: "Overdue" }[
                          task.task_status
                        ] ||
                          task.task_status ||
                          "—"}
                      </span>
                    </Link>
                  ))}
                </div>
              </DataState>
            </section>
          </div>
          <div className="ar-bottom-row">
            {permissions.projects && (
              <section className="ar-card">
                {heading(FiFolder, "Your Workspaces", "/admin/work-dashboard")}
                <DataState source={workspaces} empty="No workspaces available.">
                  <div className="ar-workspace-directory">
                    {[...workspaces.data]
                      .reverse()
                      .slice(0, 5)
                      .map((workspace, index) => (
                        <Link
                          className="ar-directory-item"
                          key={workspace.pk_work_id || index}
                          to="/admin/Task-management"
                          onClick={() =>
                            sessionStorage.setItem(
                              "property",
                              JSON.stringify(workspace),
                            )
                          }
                        >
                          <span
                            className="ar-avatar"
                            style={{
                              background: workspace.colourcode || "#eaf2ff",
                            }}
                          >
                            <FiFolder />
                          </span>
                          <div>
                            <strong>
                              {workspace.workspace_name || "Workspace"}
                            </strong>
                            <p>
                              {workspace.project_name || "No project assigned"}
                            </p>
                          </div>
                          <span className="ar-member-count">
                            <FiUsers />
                            {Array.isArray(workspace.lettersdata)
                              ? workspace.lettersdata.length
                              : "—"}
                          </span>
                          <FiArrowRight />
                        </Link>
                      ))}
                  </div>
                </DataState>
              </section>
            )}
            <section className="ar-card">
              {heading(
                FiList,
                "Task Status by Project",
                "/admin/task-overview",
              )}
              <p className="ar-note">
                Top five projects ·{" "}
                {period === "month" ? "this month" : "last 7 days"}
              </p>
              <DataState
                source={tasks}
                empty="No project tasks in the selected period."
              >
                <div className="ar-chart">
                  <Bar data={projectStatusChart} options={chartOptions} />
                </div>
              </DataState>
            </section>
          </div>
        </main>
        <aside className="ar-aside">
          {permissions.attendance && (
            <section className="ar-card">
              {heading(
                FiCheckCircle,
                "Today's Attendance",
                "/admin/attendance-management",
              )}
              <DataState source={attendance} empty="No attendance records.">
                <div className="ar-doughnut-row">
                  <div className="ar-doughnut ar-attendance-ring">
                    <Doughnut
                      data={
                        recorded
                          ? attendanceChart
                          : {
                              labels: ["No marked attendance"],
                              datasets: [
                                {
                                  data: [1],
                                  backgroundColor: ["#e6edf7"],
                                  borderWidth: 0,
                                },
                              ],
                            }
                      }
                      options={
                        recorded
                          ? doughnutOptions
                          : {
                              ...doughnutOptions,
                              plugins: {
                                legend: { display: false },
                                tooltip: { enabled: false },
                              },
                            }
                      }
                    />
                    <div className="ar-chart-center">
                      <strong>
                        {recorded ? `${presentPercentage}%` : "—"}
                      </strong>
                      <small>Present*</small>
                    </div>
                  </div>
                  <div className="ar-legend">
                    {[
                      ["present", "Present", "#24ce8c"],
                      ["absent", "Absent", "#fb536c"],
                      ["halfDay", "Half Day", "#779bd0"],
                      ["onLeave", "On Leave", "#cddcf0"],
                      ["unmarked", "Unmarked", "#e9eef5"],
                    ].map(([key, label, color]) => (
                      <div key={key}>
                        <i style={{ background: color }} />
                        <span>{label}</span>
                        <strong>{attendanceTotals[key]}</strong>
                      </div>
                    ))}
                  </div>
                </div>
              </DataState>
            </section>
          )}
          <section className="ar-card ar-calendar">
            <div className="ar-heading">
              <h2>
                <span className="ar-icon">
                  <FiCalendar />
                </span>
                Weekly Calendar
              </h2>
              <div>
                <small>
                  {weekStart.toLocaleDateString("en-GB", {
                    month: "long",
                    year: "numeric",
                  })}
                </small>
                <button
                  type="button"
                  aria-label="Previous week"
                  onClick={() => setWeekOffset((value) => value - 1)}
                >
                  <FiChevronLeft />
                </button>
                <button
                  type="button"
                  aria-label="Next week"
                  onClick={() => setWeekOffset((value) => value + 1)}
                >
                  <FiChevronRight />
                </button>
              </div>
            </div>
            <div className="ar-week">
              {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map(
                (day, index) => {
                  const date = new Date(weekStart);
                  date.setDate(date.getDate() + index);
                  return (
                    <div
                      key={day}
                      className={
                        date.toDateString() === now.toDateString()
                          ? "is-today"
                          : ""
                      }
                    >
                      <span>{day}</span>
                      <strong>{date.getDate()}</strong>
                    </div>
                  );
                },
              )}
            </div>
          </section>
          {permissions.people && (
            <section className="ar-card">
              {heading(
                FiGift,
                "Birthdays This Month",
                "/admin/employee-management",
              )}
              <DataState source={people}>
                {birthdays.length ? (
                  <div className="ar-birthdays">
                    {birthdays.map((person, index) => (
                      <div key={person.emp_id || index}>
                        <span className="ar-avatar">
                          {String(person.firstname || "?").charAt(0)}
                        </span>
                        <strong>{person.firstname}</strong>
                        <time>
                          {new Date(person.dob).toLocaleDateString("en-GB", {
                            day: "numeric",
                            month: "short",
                          })}
                        </time>
                        <FiCalendar />
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="ar-empty">No birthdays this month.</p>
                )}
              </DataState>
            </section>
          )}
          {permissions.policy && (
            <section className="ar-card">
              {heading(FiFileText, "Company Policy", "/admin/policy")}
              <DataState
                source={policies}
                empty="No company policies available."
              >
                <div className="ar-policies">
                  {policies.data.map((policy, index) => (
                    <div
                      className="ar-policy"
                      key={policy.pk_policy_id || index}
                    >
                      <span className="ar-icon">
                        <FiFileText />
                      </span>
                      <div>
                        <strong>{policy.policy_name}</strong>
                        <p>
                          Updated:{" "}
                          {policy.policy_indate
                            ? new Date(policy.policy_indate).toLocaleDateString(
                                "en-GB",
                              )
                            : "—"}
                        </p>
                      </div>
                      {policy.image_url && (
                        <a
                          href={policy.image_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label={`View ${policy.policy_name}`}
                        >
                          <FiEye />
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              </DataState>
              <a
                href={`https://billpunch.com/OfferLetter/pdfdownload.php?token=${user?.login_token}`}
                download
                className="ar-offer"
              >
                <FiDownload />
                Download Your Offer Letter
                <FiArrowRight />
              </a>
            </section>
          )}
        </aside>
      </div>
      <details className="ar-more">
        <summary>Payroll &amp; Quick Access</summary>
        <div className="ad-quick-actions">
          {[
            {
              label: "Employee Management",
              to: "/admin/employee-management",
              icon: FiUsers,
            },
            {
              label: "Create Workspace",
              to: "/admin/create-workspace",
              icon: FiPlus,
            },
            {
              label: "Approve Leave",
              to: "/admin/LeaveApprovalAdmin",
              icon: FiCheckCircle,
            },
            {
              label: "View Payroll",
              to: "/admin/payroll-management",
              icon: FiCreditCard,
            },
          ]
            .filter((item) => allowedRoute(item.to))
            .map(({ label, to, icon: Icon }) => (
              <Link key={to} to={to}>
                <Icon />
                {label}
              </Link>
            ))}
        </div>
        <div className="ar-middle-row">
          {permissions.payroll && (
            <section className="ar-card">
              <div className="ar-heading">
                <h2>Salary Payout</h2>
                <select
                  aria-label="Payout year"
                  value={year}
                  onChange={(event) => setYear(Number(event.target.value))}
                >
                  {Array.from(
                    { length: 5 },
                    (_, index) => now.getFullYear() - index,
                  ).map((value) => (
                    <option key={value}>{value}</option>
                  ))}
                </select>
              </div>
              <DataState source={payouts}>
                <div className="ar-chart">
                  <Bar data={chartData} options={chartOptions} />
                </div>
              </DataState>
            </section>
          )}
          {permissions.expenses && (
            <section className="ar-card">
              <div className="ar-heading">
                <h2>Expense Payroll</h2>
                <select
                  aria-label="Expense month"
                  value={month}
                  onChange={(event) => setMonth(Number(event.target.value))}
                >
                  {monthNames.map((name, index) => (
                    <option key={name} value={index}>
                      {name}
                    </option>
                  ))}
                </select>
              </div>
              <DataState source={expenses}>
                {[
                  ["Generated", payroll.total_generated],
                  ["Processed", payroll.processed],
                  ["Unprocessed", payroll.unprocessed],
                ].map(([label, value]) => (
                  <div className="ad-payroll-row" key={label}>
                    <span>{label}</span>
                    <strong>{money(value)}</strong>
                  </div>
                ))}
              </DataState>
            </section>
          )}
        </div>
      </details>
    </div>
  );
}

function AdminProfilePhoto({ imageUrl, name }) {
  const [failedUrl, setFailedUrl] = useState(null);
  return (
    <div className="ar-admin-photo">
      {imageUrl && failedUrl !== imageUrl ? (
        <img
          src={imageUrl}
          alt={`${name}'s profile`}
          onError={() => setFailedUrl(imageUrl)}
        />
      ) : (
        <span aria-label={name}>{name.charAt(0).toUpperCase()}</span>
      )}
    </div>
  );
}
